import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CepService } from 'src/app/app-core/lib/cep.service';
import { IbgeService } from 'src/app/app-core/lib/ibge.service';

@Component({
    selector: 'app-acquirer-itbi',
    templateUrl: './acquirer-itbi.component.html',
    styleUrls: ['./acquirer-itbi.component.scss'],
    providers: [IbgeService, CepService]
})
export class AcquirerItbiComponent implements OnInit, OnChanges {

    @Output() private onAcquirerChange = new EventEmitter();
    @Input() isRepresentingAnotherPerson: boolean = false;
    @Input() acquirerDetails;
    @Input() acquirerDisabled: boolean = false;

    public form: FormGroup;
    public estados: [];
    public municipiosPorEstado: any[];

    constructor(
        private fb: FormBuilder,
        private ibgeService: IbgeService,
        private cepService: CepService
    ) { }

    ngOnInit() {
        this.initForm();
        this.formChanges();
        this.loadUfs();
        this.loadMunicipiosByUF();
        this.handleFindCep();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.form) return;

        if (changes['acquirerDetails'] && this.acquirerDetails && !this.acquirerDisabled) {
            this.changeDataRequester();
            this.form.get('CPF')?.disable();
            this.form.get('nome')?.disable();
            this.form.get('email')?.disable();
            this.form.get('telefone')?.disable();
        }
    }

    public changeDataRequester() {
        if (!this.acquirerDetails) return;

        const currentCpf = this.form.get('CPF')?.value;
        const currentNome = this.form.get('nome')?.value;
        const currentEmail = this.form.get('email')?.value;
        const currentTelefone = this.form.get('telefone')?.value;

        const newCpf = this.acquirerDetails.SOLR_CpfCnpj;
        const newNome = this.acquirerDetails.SOLR_NomeRazaoSocial;
        const newEmail = this.acquirerDetails.SOLR_Email;
        const newTelefone = this.acquirerDetails.SOLR_Telefone;

        if (currentCpf === newCpf && currentNome === newNome && currentEmail === newEmail && currentTelefone === newTelefone ) return;

        this.form.patchValue({
            CPF: newCpf,
            nome: newNome,
            email: newEmail,
            telefone: newTelefone
        });

        this.form.updateValueAndValidity();
    }

    private initForm() {
        this.form = this.fb.group({
            CPF: ['', Validators.required],
            nome: ['', Validators.required],
            email: ['', Validators.required],
            telefone: ['', Validators.required],
            localidade: ['', Validators.required],
            bairro: ['', Validators.required],
            logradouro: ['', Validators.required],
            uf: ['', Validators.required],
            numero: ['', Validators.required],
            complemento: [''],
            CEP: ['', Validators.required],
            tipoImovel: ['Urbano', Validators.required],
            naturezaImovel: ['Territorial', Validators.required],
            transacaoInscricao: [''],
            transacaoEndereco: ['', Validators.required],
            transacaoNumero: ['', Validators.required],
            transacaoComplemento: [''],
            transacaoLote: ['', Validators.required],
            transacaoQuadra: ['', Validators.required],
            transacaoMatricula: ['', Validators.required],
            transacaoAreaTerreno: ['', Validators.required],
            transacaoAreaTotal: ['', Validators.required],
            percentualTransmitido: ['', Validators.required],
            transacaoValorFinanciado: ['', Validators.required],
            transacaoValorNaoFinanciado: ['', Validators.required],
            transacaoTotalDeclarado: ['', Validators.required]
        });
    }

    private formChanges() {
        this.form.valueChanges.subscribe(() => {
            this.onAcquirerChange.emit(this.form.getRawValue());
        });
    }

    /**
     * Dados do rascunho. Os campos travados (CPF, nome, e-mail e telefone de quem está
     * logado ou representado) continuam com o que o formulário já preencheu.
     */
    public restoreDraft(valores: any): void {
        if (!valores) return;

        const { CEP, ...demais } = valores;
        const liberados: any = {};

        Object.keys(demais)
            .filter(campo => this.form.get(campo)?.enabled)
            .forEach(campo => liberados[campo] = demais[campo]);

        this.form.patchValue(liberados);
        // Sem a busca do CEP, que trocaria o endereço preenchido pelo da base dos Correios.
        this.form.get('CEP').setValue(CEP ?? '', { emitEvent: false });
        this.form.updateValueAndValidity();
    }

    reset() {
        this.form.patchValue({
            CPF: '',
            nome: '',
            email: '',
            telefone: '',
            localidade: '',
            bairro: '',
            logradouro: '',
            uf: '',
            numero: '',
            complemento: '',
            CEP: '',
            tipoImovel: null,
            naturezaImovel: null,
            transacaoInscricao: '',
            transacaoEndereco: '',
            transacaoNumero: '',
            transacaoComplemento: '',
            transacaoLote: '',
            transacaoQuadra: '',
            transacaoMatricula: '',
            transacaoAreaTerreno: '',
            transacaoAreaTotal: '',
            percentualTransmitido: '',
            transacaoValorFinanciado: '',
            transacaoValorNaoFinanciado: '',
            transacaoTotalDeclarado: ''
        });
        this.form.enable();
        this.form.updateValueAndValidity();
    }

    private loadUfs() {
        this.ibgeService.ufs.subscribe({
            next: (response: any) => {
                this.estados = response;
            }
        });
    }

    public loadMunicipiosByUF() {
        this.form.get('uf').valueChanges.subscribe((value: any) => {
            this.ibgeService.municipiosByUf(value).subscribe({
                next: (response: any) => {
                    this.municipiosPorEstado = response;
                }
            });
        })
    }

    public handleFindCep() {
        this.form.get('CEP').valueChanges.subscribe({
            next: (value: string) => {
                if (value.length > 7) {
                    this.cepService.getEnderecoByCep(value).subscribe({
                        next: (response: any) => {
                            this.form.patchValue({
                                bairro: response.bairro,
                                logradouro: response.logradouro,
                                localidade: response.localidade
                            });

                            if (!this.form.get('uf').value || this.form.get('uf').value != response.uf) {
                                this.form.get('uf').setValue(response.uf);
                            }

                            this.form.updateValueAndValidity();
                        }
                    });
                }
            }
        });
    }
}
